import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronUp, Landmark } from "lucide-react";
import { adminFinanceGlobal, adminExpenses, adminAddExpense, adminDeleteExpense } from "@/lib/afmApi";
import { Loading } from "@/components/common";
import { Expenses, ranges, fmtMoney as money } from "@/components/ManagerFinance";

// Solo Always Fresh: el total de cada compañía, la parte de Always y la de cada compañía y artista.
const COLS = [
  { k: "income", label: "Ingresos", cls: "text-black" },
  { k: "label", label: "Always ($)", cls: "text-[#AC3333] font-semibold" },
  { k: "company", label: "Compañías", cls: "text-slate-700" },
  { k: "artist", label: "Artistas", cls: "text-slate-500" },
];

export default function AdminFinanzas() {
  const opts = useMemo(ranges, []);
  const [range, setRange] = useState("3");
  const [mid, setMid] = useState("");
  const [d, setD] = useState(undefined);
  const [open, setOpen] = useState(null);
  const r = opts.find((o) => o.key === range) || opts[0];
  const load = useCallback(() => {
    setD(undefined);
    adminFinanceGlobal(r.desde, r.hasta).then(setD).catch((e) => setD({ error: e?.response?.data?.detail || "No se pudo cargar" }));
  }, [r.desde, r.hasta]);
  useEffect(() => { load(); }, [load]);
  const expApi = useMemo(() => (mid ? {
    list: (params) => adminExpenses(mid, params), add: (body) => adminAddExpense(mid, body), remove: (id) => adminDeleteExpense(mid, id),
  } : null), [mid]);

  const companies = d?.companies || [];
  const shown = mid ? companies.filter((c) => c.id === mid) : companies;
  const totals = useMemo(() => {
    if (!mid) return d?.totals || {};
    return shown[0]?.totals || {};
  }, [d, mid, shown]);
  const company = companies.find((c) => c.id === mid);

  return (
    <div className="space-y-6" data-testid="admin-finanzas">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex-1">
          <h1 className="flex items-center gap-2 font-serif text-3xl font-extrabold tracking-tight text-black"><Landmark className="h-7 w-7" /> Finanzas de sellos</h1>
          <p className="mt-1 text-sm text-slate-500">Lo que pagó la distribuidora por los artistas de tus compañías y cuánto le toca a Always. Esto solo lo ves tú; cada compañía ve desde su parte.</p>
        </div>
        <select value={mid} onChange={(e) => setMid(e.target.value)} className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm">
          <option value="">Todas las compañías</option>
          {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select value={range} onChange={(e) => setRange(e.target.value)} className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm capitalize">
          {opts.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
        </select>
      </div>

      {d === undefined && <Loading />}
      {d?.error && <p className="text-sm text-slate-500">{d.error}</p>}
      {d && !d.error && (
        <>
          {Object.entries(totals).map(([cur, t]) => (
            <div key={cur} className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {COLS.map((c) => (
                <div key={c.k} className="rounded-xl border border-slate-200 bg-white p-4"><div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{c.label}</div><div className={`mt-1 font-mono text-lg tabular-nums ${c.cls}`}>{money(t[c.k], cur)}</div></div>
              ))}
            </div>
          ))}
          {!Object.keys(totals).length && <p className="rounded-xl border border-dashed border-slate-300 px-5 py-6 text-center text-sm text-slate-500">Aún no hay reportes de regalías para este período.</p>}

          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
                  <th className="px-5 py-3 font-semibold">Compañía / artista</th>
                  <th className="px-3 py-3 font-semibold">% Always</th>
                  {COLS.map((c) => <th key={c.k} className="px-3 py-3 text-right font-semibold">{c.label}</th>)}
                  <th className="px-5 py-3 text-right font-semibold">Gastos</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((c) => {
                  const curs = Object.keys(c.totals || {});
                  return (
                    <Fragment key={c.id}>
                      {(curs.length ? curs : [null]).map((cur, i) => (
                        <tr key={`${c.id}-${cur || "x"}`} className={i === 0 ? "border-t border-slate-200" : ""}>
                          <td className="px-5 py-2.5">{i === 0 && <button onClick={() => setOpen(open === c.id ? null : c.id)} className="flex items-center gap-1 font-semibold text-black hover:underline">{c.name} {open === c.id ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}</button>}</td>
                          <td className="whitespace-nowrap px-3 py-2.5 text-xs text-slate-500">{i === 0 && `${c.label_pct}% por defecto`}</td>
                          {COLS.map((k) => <td key={k.k} className={`whitespace-nowrap px-3 py-2.5 text-right font-mono tabular-nums ${k.cls}`}>{cur ? money(c.totals[cur][k.k], cur) : "—"}</td>)}
                          <td className="whitespace-nowrap px-5 py-2.5 text-right font-mono tabular-nums text-amber-700">{i === 0 ? Object.entries(c.expenses_total || {}).map(([x, v]) => money(v, x)).join(" · ") || "—" : ""}</td>
                        </tr>
                      ))}
                      {open === c.id && c.artists.map((a) => {
                        const ac = Object.keys(a.totals || {});
                        return (ac.length ? ac : [null]).map((cur, i) => (
                          <tr key={`${c.id}-${a.id}-${cur || "x"}`} className="bg-slate-50 text-xs">
                            <td className="py-2 pl-10 pr-5 text-slate-700">{i === 0 && a.name}</td>
                            <td className="whitespace-nowrap px-3 py-2 text-slate-500">{i === 0 && `${a.label_pct}% · ${a.artist_pct != null ? `artista ${a.artist_pct}%` : "sin reparto"}`}</td>
                            {COLS.map((k) => <td key={k.k} className={`whitespace-nowrap px-3 py-2 text-right font-mono tabular-nums ${k.cls}`}>{cur ? money(a.totals[cur][k.k], cur) : "—"}</td>)}
                            <td className="whitespace-nowrap px-5 py-2 text-right font-mono tabular-nums text-amber-700">{i === 0 ? Object.entries(a.expenses || {}).map(([x, v]) => money(v, x)).join(" · ") || "—" : ""}</td>
                          </tr>
                        ));
                      })}
                    </Fragment>
                  );
                })}
                {!shown.length && <tr><td colSpan={7} className="px-5 py-10 text-center text-sm text-slate-400">Todavía no hay compañías.</td></tr>}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-slate-500">Datos reales de la distribuidora. El dinero de un tema cuenta en el artista que lo saca. Cada moneda va por separado.</p>
        </>
      )}

      {company && expApi
        ? <Expenses key={company.id} artists={company.artists} onChange={load} api={expApi} isLabel />
        : <p className="text-sm text-slate-500">Elige una compañía arriba para ver o registrar gastos en sus artistas.</p>}
    </div>
  );
}
